import { competitionPath, legacyPath } from 'views/TradingCompetitionV3/routes'
import type { CompetitionCounts } from './types'

/** Grouping of a token's competitions for its tabs. Pure, so the rules are tested once. */
export const FINISHED_PHASES = ['settled', 'refunded', 'awaiting_result', 'refundable'] as const

type Row = { id: number; phase: string; startTime: number; endTime: number }
export type Grouped<T extends Row> = { active: T[]; upcoming: T[]; history: T[] }

/** Active = live, soonest end first. Upcoming = not started, soonest start first. History = settled, refunded or waiting for a result, newest first. */
export function groupCompetitions<T extends Row>(rows: T[]): Grouped<T> {
  return {
    active: rows.filter((r) => r.phase === 'live').sort((a, b) => a.endTime - b.endTime || b.id - a.id),
    upcoming: rows.filter((r) => r.phase === 'upcoming').sort((a, b) => a.startTime - b.startTime || b.id - a.id),
    history: rows.filter((r) => (FINISHED_PHASES as readonly string[]).includes(r.phase)).sort((a, b) => b.id - a.id),
  }
}

/** The tab to open first: the first group that has something, else Active. */
export function firstTab(g: Grouped<Row>): 'active' | 'upcoming' | 'history' {
  return g.active.length ? 'active' : g.upcoming.length ? 'upcoming' : g.history.length ? 'history' : 'active'
}

// ---- both contracts (site review 59 #1, 2026-10-07: the CADINU page said "Competitions 0" while V4 Competition #1 was live) ----

const lower = (a?: string) => (a ?? '').toLowerCase()

/**
 * One list for a token: the current contract's competitions, then the previous contract's tagged `legacy` (ids restart per contract, so
 * a row is known by both). Only rows whose project token is this token: an older API that ignores the filter cannot add another token's.
 */
export function mergeTokenCompetitions<T extends { id: number; projectToken?: string }>(current: T[] | undefined, legacy: T[] | undefined, token?: string): Array<T & { legacy?: boolean }> {
  const mine = (r: T) => lower(r.projectToken) === lower(token)
  return [...(current ?? []).filter(mine), ...(legacy ?? []).filter(mine).map((r) => ({ ...r, legacy: true }))]
}

/** Where a row opens: the current contract under /trading-competition/<id>, the previous one under /legacy. */
export const competitionHref = (r: { id: number; legacy?: boolean }) => (r.legacy ? legacyPath(r.id) : competitionPath(r.id))

/** Live / upcoming / finished counts per project token, from a plain list. */
export function countCompetitions(rows: Array<{ id: number; projectToken?: string; phase: string }>): Record<string, CompetitionCounts> {
  const out: Record<string, CompetitionCounts> = {}
  for (const c of rows) {
    const t = lower(c.projectToken)
    if (!/^0x[0-9a-f]{40}$/.test(t)) continue
    if (!out[t]) out[t] = { live: 0, upcoming: 0, finished: 0, total: 0, lastId: c.id }
    const o = out[t]
    o.total += 1
    if (c.phase === 'live') o.live += 1
    else if (c.phase === 'upcoming') o.upcoming += 1
    else o.finished += 1
    o.lastId = Math.max(o.lastId ?? 0, c.id)
  }
  return out
}

/** Two contracts' counts, added per token. */
export function addCounts(a: Record<string, CompetitionCounts>, b: Record<string, CompetitionCounts>): Record<string, CompetitionCounts> {
  const out: Record<string, CompetitionCounts> = { ...a }
  Object.entries(b).forEach(([k, v]) => {
    const t = lower(k)
    const x = out[t]
    out[t] = x
      ? { live: x.live + v.live, upcoming: x.upcoming + v.upcoming, finished: x.finished + v.finished, total: x.total + v.total, lastId: Math.max(x.lastId ?? 0, v.lastId ?? 0) }
      : { ...v }
  })
  return out
}
